import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, unref as _unref } from "vue"

const _hoisted_1 = { class: "row w-450" }

import { ref, computed } from 'vue';
import { formatFavoriteFirstPhase, formatFavoriteSuffix, parseFavorite, emptyParsedDose } from '@/functions/dosageUtils';

import { useCurrentEntryStore } from '@/stores/currentEntry';
import { useReferenceStore } from '@/stores/reference';
import { useAppUserStore } from '@/stores/appUser';
import { findMedicationPreference } from '@/functions/preferenceUtils';
import { resolveSingleSuggestionWithTitle } from '@/functions/documentSuggestions'
import { getSingleElement } from '@/utils/array-utils';
import { useUiStore } from '@/stores/ui';
import { toastError } from "@/utils/toast-utils";
import { useRoute } from 'vue-router';
import { useToast } from "primevue";
import { useI18n } from 'vue-i18n';


export default /*@__PURE__*/_defineComponent({
  __name: 'addMedicLine',
  props: {
  content: {
    type: Object,
    required: true,
  },
  //conclusionId = very important for saving data to the correct ccl
  conclusionId: {
    type: String,
    default: '',
  },
  switchMedication: {
    type: Boolean,
    required: false,
  },
  treatmentAdded: {
    type: Function,
    required: false
  }
},
  emits: ['close', 'closeAndShow'],
  setup(__props, { emit: __emit }) {

const { t } = useI18n();
const toast = useToast();
const appUserStore = useAppUserStore();
const currentEntryStore = useCurrentEntryStore();
const referenceStore = useReferenceStore();
const route = useRoute();
const uiStore = useUiStore()

const props = __props
const emits = __emit

const creationLocked = ref(false)

const medicName = computed(() => {
  return props.content.matchedText || props.content.originalText;
})

const medicFavorite = computed(() => {
  const suggestions = appUserStore.currentAppUserData.preferences.medicationSuggestions || []
  const match = findMedicationPreference(suggestions, medicName.value, props.content?.sctSubstanceKey)
  if (!match) return null;
  return resolveSingleSuggestionWithTitle(match.suggestions);
})

const medicContent = computed(() => {
  if (!props.content.multiDosageText) return null
  return resolveSingleSuggestionWithTitle(props.content.multiDosageText)
})

const existingMedics = computed(() => {
  const sctSubstanceKey = props.content?.sctSubstanceKey
  const name = props.content?.name?.toLowerCase()
  return referenceStore.existingMedicList.filter(medic => {
    if (sctSubstanceKey && medic.sctSubstanceKey === sctSubstanceKey) return true
    if (name && medic.name?.toLowerCase() === name) return true
    return false
  })
})

async function reloadMedicSearchReferences() {
  if (!currentEntryStore.hasCurrentEntry) return;
  const allExpressions = currentEntryStore.patientHistoryResponse;
  if (!allExpressions.length) return;  
  referenceStore.createMedicSearchReferences(allExpressions, false, getSingleElement(route.params.patientId))
}

async function fastCreateNewMedicFromFavorite(newMultiDosage: string) {
  if (creationLocked.value === true) {console.log("Blocked double request"); return;}
  creationLocked.value = true
  const oldSapExpr = currentEntryStore.poppedSapExpr;
  const shouldSwitch = props.switchMedication && oldSapExpr?.expressionCcl?.expressionCclId;
  const conclusionId = props.conclusionId
  const sapName = medicName.value
  const sctSubstanceKey = props.content?.sctSubstanceKey
  const parsedDoses = parseFavorite(newMultiDosage)
  currentEntryStore.clearPoppedSapExpr();
  emits('close')
  try {
    if (shouldSwitch) {
      const [newSapExpr] = await Promise.all([
        currentEntryStore.createNewSapExpr(conclusionId, sapName, '', '', parsedDoses, sctSubstanceKey),
        currentEntryStore.deleteOrStopSap(oldSapExpr),
      ])
      if (uiStore.dashBoardItem) {
        currentEntryStore.setPoppedSapExpr(newSapExpr);
        uiStore.setDashBoardItem(newSapExpr);
      }
    } else {
      await currentEntryStore.createNewSapExpr(conclusionId, sapName, '', '', parsedDoses, sctSubstanceKey)
    }

    if (props.treatmentAdded) {
      await props.treatmentAdded();
    }

    await currentEntryStore.reloadAfterMedicationChange()
    reloadMedicSearchReferences()
  } finally {
    creationLocked.value = false
  }
}

async function createNewMedicAndShow() {
  if (creationLocked.value) return;
  creationLocked.value = true
  const oldSapExpr = currentEntryStore.poppedSapExpr;
  const conclusionId = props.conclusionId
  const sapName = medicName.value
  const sctSubstanceKey = props.content?.sctSubstanceKey
  const shouldSwitch = props.switchMedication
  const doses = [emptyParsedDose()];
  const optimisticExpr = {
    ...props.content,
    matchedText: sapName,
    sctAdministrationRouteTerm: '',
    galenic: '',
    multiDosageText: '',
    durationType: 'Duration',
    isOptimistic: true,
  }

  try {
    currentEntryStore.setPoppedSapExpr(optimisticExpr)
    //Backend
    const [newSapExpr] = await Promise.all([
      currentEntryStore.createNewSapExpr(conclusionId, sapName, '', '', doses, sctSubstanceKey),
      ...(shouldSwitch ? [currentEntryStore.deleteOrStopSap(oldSapExpr)] : []),
    ])
    //Frontend
    currentEntryStore.patchPoppedSapExpr(newSapExpr);
    if (!shouldSwitch) emits('closeAndShow');
    if (shouldSwitch && uiStore.dashBoardItem) {
      uiStore.setDashBoardItem(newSapExpr);
    }

    if (props.treatmentAdded) {
      await props.treatmentAdded();
    }

    await currentEntryStore.reloadAfterMedicationChange()
    reloadMedicSearchReferences()
  } catch (e) {
    currentEntryStore.setPoppedSapExpr(oldSapExpr);
    toastError(toast, { summary: t('common.toast.error'), detail: t('error.DEFAULT') });
    if (!shouldSwitch) emits('close');
  } finally {
    creationLocked.value = false;
  }
}

function selectAndShowPoppedSapExpr(sapExpr: any) {
  currentEntryStore.setPoppedSapExpr(sapExpr)
  emits('closeAndShow')
}

const locale = computed(() => 
  appUserStore?.currentAppUserData?.preferences?.menuLanguageIso || 'fr'
)


return (_ctx: any,_cache: any) => {
  const _component_text_item = _resolveComponent("text-item")!
  const _component_buttonGroup = _resolveComponent("buttonGroup")!
  const _component_svg_item = _resolveComponent("svg-item")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (existingMedics.value.length)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createVNode(_component_buttonGroup, {
            styles: "pad-s",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (createNewMedicAndShow()))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_text_item, { txt: medicName.value }, null, 8, ["txt"])
            ]),
            _: 1
          }),
          _createVNode(_component_buttonGroup, {
            class: "row mleft-10",
            styles: "pad-s",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (selectAndShowPoppedSapExpr(existingMedics.value[0])))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_svg_item, {
                icon: "evolve",
                class: "mtop-01 txt-primary"
              }),
              _createVNode(_component_text_item, {
                txt: existingMedics.value[0].matchedText,
                class: "mleft-04 txt-primary"
              }, null, 8, ["txt"]),
              (props.content.hasAllergy)
                ? (_openBlock(), _createBlock(_component_svg_item, {
                    key: 0,
                    icon: "allergy",
                    styles: "main-alert",
                    class: "mtop-01 mleft-04"
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _cache[5] || (_cache[5] = _createElementVNode("div", { class: "mleft-20" }, null, -1))
        ], 64))
      : _createCommentVNode("", true),
    (!existingMedics.value.length)
      ? (_openBlock(), _createBlock(_component_buttonGroup, {
          key: 1,
          styles: "pad-s",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (createNewMedicAndShow())),
          class: _normalizeClass(props.content.multiDosageText ? 'max-w-250' : '')
        }, {
          default: _withCtx(() => [
            _createVNode(_component_text_item, { txt: medicName.value }, null, 8, ["txt"]),
            (props.content.hasAllergy)
              ? (_openBlock(), _createBlock(_component_svg_item, {
                  key: 0,
                  icon: "allergy",
                  styles: "size-m main-alert",
                  class: "mtop-01 mleft-04",
                  style: {"margin-top":"0.11rem"}
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["class"]))
      : _createCommentVNode("", true),
    (!existingMedics.value.length)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
          (medicContent.value)
            ? (_openBlock(), _createBlock(_component_buttonGroup, {
                key: 0,
                styles: "pad-s",
                class: "mleft-05",
                onClick: _cache[3] || (_cache[3] = ($event: any) => (fastCreateNewMedicFromFavorite(medicContent.value.text)))
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_svg_item, {
                    icon: "play",
                    styles: "main-m",
                    class: "mtop-01"
                  }),
                  (medicContent.value.title)
                    ? (_openBlock(), _createBlock(_component_text_item, {
                        key: 0,
                        txt: medicContent.value.title,
                        styles: "main-m",
                        class: "mleft-01"
                      }, null, 8, ["txt"]))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createVNode(_component_text_item, {
                          txt: _unref(formatFavoriteFirstPhase)(medicContent.value.text, locale.value),
                          styles: "main-m",
                          class: "mleft-01 mw-300"
                        }, null, 8, ["txt"]),
                        (_unref(formatFavoriteSuffix)(medicContent.value.text))
                          ? (_openBlock(), _createBlock(_component_text_item, {
                              key: 0,
                              txt: _unref(formatFavoriteSuffix)(medicContent.value.text),
                              styles: "main-l",
                              class: "mleft-08",
                              style: {"flex-shrink":"0"}
                            }, null, 8, ["txt"]))
                          : _createCommentVNode("", true)
                      ], 64))
                ]),
                _: 1
              }))
            : (medicFavorite.value)
              ? (_openBlock(), _createBlock(_component_buttonGroup, {
                  key: 1,
                  styles: "pad-s",
                  class: "mleft-05",
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (fastCreateNewMedicFromFavorite(medicFavorite.value.text)))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_svg_item, {
                      icon: "play",
                      styles: "main-m",
                      class: "mtop-01"
                    }),
                    (medicFavorite.value.title)
                      ? (_openBlock(), _createBlock(_component_text_item, {
                          key: 0,
                          txt: medicFavorite.value.title,
                          styles: "main-m",
                          class: "mleft-01"
                        }, null, 8, ["txt"]))
                      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createVNode(_component_text_item, {
                            txt: _unref(formatFavoriteFirstPhase)(medicFavorite.value.text, locale.value),
                            styles: "main-m",
                            class: "mleft-01 mw-300"
                          }, null, 8, ["txt"]),
                          (_unref(formatFavoriteSuffix)(medicFavorite.value.text))
                            ? (_openBlock(), _createBlock(_component_text_item, {
                                key: 0,
                                txt: _unref(formatFavoriteSuffix)(medicFavorite.value.text),
                                styles: "main-l",
                                class: "mleft-08"
                              }, null, 8, ["txt"]))
                            : _createCommentVNode("", true)
                        ], 64))
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true)
  ]))
}
}

})